// Generic "toggleable category" tab used by Exterior and Systems modules.
import { useEffect, useMemo } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  exteriorItemTotal, exteriorTotal, SCOPE_OPTIONS, CONDITION_OPTIONS, SCOPE_MULT,
} from "@/lib/exteriorTemplates";
import { useFormatCurrency } from "@/lib/CurrencyContext";
import { effectiveRate, currencyMultiplier } from "@/lib/regionalPresets";
import { exteriorCategoryMultiplier } from "@/lib/constructionTypes";
import { scopeMultiplierHint } from "@/lib/calcHints";
import CalcHelp from "@/components/CalcHelp";

function cryptoRandomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "cat-" + Math.random().toString(36).slice(2);
}

function ensureItems(existing = [], floorAreaSqm = 0, categories = [], overrides = null, currency = "USD", project = null, applyConstructionMult = false) {
  const byKey = new Map(existing.map((i) => [i.category, i]));
  return categories.map((c) => {
    const prev = byKey.get(c.key);
    if (prev) return prev;
    const qty = typeof c.defaultQty === "function" ? c.defaultQty(floorAreaSqm) : c.defaultQty;
    const constructionMult = applyConstructionMult ? exteriorCategoryMultiplier(c.key, project) : 1.0;
    const rate = Math.round(effectiveRate(c.baseRate, overrides?.[c.key], currency) * constructionMult);
    return {
      id: cryptoRandomId(),
      category: c.key,
      label: c.label,
      enabled: false,
      quantity: qty,
      unit: c.unit,
      condition: "tired",
      scope: "replace",
      rate,
    };
  });
}

export default function CategoryToggleTab({
  project,
  onChange,
  field,               // 'exterior' | 'systems'
  categories,          // array of {key,label,unit,baseRate,defaultQty,icon}
  title,
  description,
  note,                // optional subtle callout shown under the heading
  totalTestId,
  cardTestPrefix,      // e.g. 'ext' or 'sys'
  rateOverrides = null, // optional map: { [category_key]: rate }
  currency = "USD",    // project currency — scales base USD template rates
}) {
  const items = project[field] || [];
  const fmt = useFormatCurrency();

  useEffect(() => {
    // Seed missing categories using Cost Library overrides when available.
    const keys = new Set(items.map((i) => i.category));
    const missing = categories.some((c) => !keys.has(c.key));
    if (missing) {
      const next = ensureItems(items, project.floor_area_sqm, categories, rateOverrides, currency, project, field === "exterior");
      onChange({ [field]: next });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateOverrides, currency, project?.construction_type, project?.roof_type, project?.foundation_type, project?.wall_finish]);

  const merged = useMemo(
    () => ensureItems(items, project.floor_area_sqm, categories, rateOverrides, currency, project, field === "exterior"),
    [items, project.floor_area_sqm, categories, rateOverrides, currency, project?.construction_type, project?.roof_type, project?.foundation_type, project?.wall_finish, field]
  );

  const total = exteriorTotal(merged);
  const enabledCount = merged.filter((i) => i.enabled).length;

  const updateItem = (id, patch) => {
    const next = merged.map((i) => (i.id === id ? { ...i, ...patch } : i));
    onChange({ [field]: next });
  };

  return (
    <div className="space-y-6 fc-fade-in" data-testid={`${field}-tab`}>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h2 className="fc-heading text-xl text-[#1C1C1C]" data-testid={`${field}-heading`}>{title}</h2>
          <p className="text-xs text-[#A8A29E] mt-1 max-w-xl" data-testid={`${field}-description`}>{description}</p>
          {note && (
            <p
              className="text-[11px] text-sage-700 mt-2 italic max-w-xl"
              data-testid={`${field}-note`}
            >
              {note}
            </p>
          )}
        </div>
        <div className="text-right fc-card fc-sage px-4 py-3 inline-block min-w-[170px]">
          <div className="fc-label">{title} Total</div>
          <div className="fc-heading text-2xl text-[#1C1C1C] mt-0.5" data-testid={totalTestId}>
            {fmt(total)}
          </div>
          <div className="text-[11px] text-sage-700 mt-0.5">
            {enabledCount} of {merged.length} enabled
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid={`${field}-grid`}>
        {merged.map((item) => {
          const meta = categories.find((c) => c.key === item.category);
          const Icon = meta?.icon;
          const itemTotal = exteriorItemTotal(item);
          const tidPrefix = `${cardTestPrefix}-`;
          return (
            <div
              key={item.id}
              role={item.enabled ? undefined : "button"}
              tabIndex={item.enabled ? undefined : 0}
              onClick={item.enabled ? undefined : () => updateItem(item.id, { enabled: true })}
              onKeyDown={item.enabled ? undefined : (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  updateItem(item.id, { enabled: true });
                }
              }}
              className={`fc-card p-5 transition-all ${
                item.enabled
                  ? ""
                  : "cursor-pointer opacity-80 hover:opacity-100 hover:border-black/[0.15]"
              }`}
              data-testid={`${tidPrefix}card-${item.category}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-black/[0.04] border border-black/[0.06] grid place-items-center text-[#27272A]">
                    {Icon ? <Icon className="w-4 h-4" /> : null}
                  </div>
                  <div className="min-w-0">
                    <div className="fc-heading text-[#1C1C1C] text-[15px] font-medium truncate">
                      {item.label}
                    </div>
                    <div className="text-[11px] text-[#A8A29E] mt-0.5">
                      {item.enabled ? fmt(itemTotal) : "Click to enable"}
                    </div>
                  </div>
                </div>
                <Switch
                  data-testid={`${tidPrefix}toggle-${item.category}`}
                  checked={item.enabled}
                  onCheckedChange={(v) => updateItem(item.id, { enabled: !!v })}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {item.enabled && (
                <>
                  {meta?.presets && meta.presets.length > 0 ? (
                    <VariantsSection
                      item={item}
                      presets={meta.presets}
                      unit={meta.unit}
                      cardTestPrefix={cardTestPrefix}
                      fmt={fmt}
                      currency={currency}
                      onVariantsChange={(variants) => updateItem(item.id, { variants })}
                    />
                  ) : (
                    <div className="mt-5 grid grid-cols-2 gap-3">
                      <MiniField label={`Quantity (${item.unit})`}>
                        <Input
                          data-testid={`${tidPrefix}qty-${item.category}`}
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(item.id, { quantity: Number(e.target.value) || 0 })}
                          className="fc-input h-9 rounded-md text-sm"
                        />
                      </MiniField>
                      <MiniField label="Rate ($/unit)">
                        <Input
                          data-testid={`${tidPrefix}rate-${item.category}`}
                          type="number"
                          value={item.rate}
                          onChange={(e) => updateItem(item.id, { rate: Number(e.target.value) || 0 })}
                          className="fc-input h-9 rounded-md text-sm"
                        />
                      </MiniField>
                      <MiniField label="Condition">
                        <MiniSelect
                          tid={`${tidPrefix}condition-${item.category}`}
                          value={item.condition}
                          onChange={(v) => updateItem(item.id, { condition: v })}
                          options={CONDITION_OPTIONS}
                        />
                      </MiniField>
                      <MiniField label="Scope">
                        <MiniSelect
                          tid={`${tidPrefix}scope-${item.category}`}
                          value={item.scope}
                          onChange={(v) => updateItem(item.id, { scope: v })}
                          options={SCOPE_OPTIONS}
                        />
                      </MiniField>
                    </div>
                  )}

                  {meta?.presets && meta.presets.length > 0 && (
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <MiniField label="Condition">
                        <MiniSelect
                          tid={`${tidPrefix}condition-${item.category}`}
                          value={item.condition}
                          onChange={(v) => updateItem(item.id, { condition: v })}
                          options={CONDITION_OPTIONS}
                        />
                      </MiniField>
                      <MiniField label="Scope">
                        <MiniSelect
                          tid={`${tidPrefix}scope-${item.category}`}
                          value={item.scope}
                          onChange={(v) => updateItem(item.id, { scope: v })}
                          options={SCOPE_OPTIONS}
                        />
                      </MiniField>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-black/[0.06] flex items-center justify-between">
                    <div className="fc-label">Line Total</div>
                    <div className="flex items-center gap-2">
                      <div className="fc-heading text-lg text-[#1C1C1C]" data-testid={`${tidPrefix}total-${item.category}`}>
                        {fmt(itemTotal)}
                      </div>
                      {Array.isArray(item.variants) && item.variants.length > 0 ? (
                        <CalcHelp
                          testId={`${tidPrefix}total-help-${item.category}`}
                          title={`${item.label} — variant roll-up`}
                          formula={`Σ(${item.variants.length} variants) × ${SCOPE_MULT[item.scope] ?? 1}× = ${fmt(itemTotal)}`}
                          lines={[
                            "Line total sums every variant row (qty × rate), then applies the scope multiplier.",
                            scopeMultiplierHint(item.scope),
                          ]}
                        />
                      ) : (SCOPE_MULT[item.scope] ?? 1) !== 1 ? (
                        <CalcHelp
                          testId={`${tidPrefix}total-help-${item.category}`}
                          title={`${item.label} — scope adjusted`}
                          formula={`${item.quantity} ${item.unit} × ${fmt(item.rate)} × ${SCOPE_MULT[item.scope]}× = ${fmt(itemTotal)}`}
                          lines={[scopeMultiplierHint(item.scope)]}
                        />
                      ) : null}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MiniField({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="fc-label mb-1">{label}</div>
      {children}
    </div>
  );
}

function MiniSelect({ tid, value, onChange, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={tid} className="fc-input rounded-md text-sm h-9">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function variantId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "var-" + Math.random().toString(36).slice(2);
}

function VariantsSection({ item, presets, unit, cardTestPrefix, fmt, currency = "USD", onVariantsChange }) {
  const variants = Array.isArray(item.variants) ? item.variants : [];
  const tid = `${cardTestPrefix}-`;
  const mult = currencyMultiplier(currency);

  const scaledPresetRate = (p) => Math.round((Number(p.rate) || 0) * mult);

  const addPreset = (p) => {
    onVariantsChange([
      ...variants,
      { id: variantId(), label: p.label, qty: 1, rate: scaledPresetRate(p) },
    ]);
  };
  const addCustom = () => {
    onVariantsChange([
      ...variants,
      { id: variantId(), label: "Custom item", qty: 1, rate: 0 },
    ]);
  };
  const updateVariant = (id, patch) => {
    onVariantsChange(variants.map((v) => (v.id === id ? { ...v, ...patch } : v)));
  };
  const removeVariant = (id) => {
    onVariantsChange(variants.filter((v) => v.id !== id));
  };

  return (
    <div className="mt-5" data-testid={`${tid}variants-${item.category}`}>
      {/* Preset chip row */}
      <div className="fc-label mb-2">Add an option</div>
      <div className="flex flex-wrap gap-1.5" data-testid={`${tid}presets-${item.category}`}>
        {presets.map((p) => {
          const shown = scaledPresetRate(p);
          return (
            <button
              key={p.label}
              type="button"
              onClick={() => addPreset(p)}
              data-testid={`${tid}preset-${item.category}-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className="text-[11px] text-[#1C1C1C] bg-white hover:bg-sage-50 border border-black/[0.08] hover:border-sage-200 rounded-full h-7 px-2.5 inline-flex items-center gap-1 transition-colors"
              title={`Add ${p.label} @ ${fmt(shown)}`}
            >
              <Plus className="w-3 h-3" /> {p.label}
              <span className="text-[#78716C] fc-mono ml-1">{fmt(shown)}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={addCustom}
          data-testid={`${tid}preset-custom-${item.category}`}
          className="text-[11px] text-sage-700 bg-sage-50 hover:bg-sage-100 border border-sage-200 rounded-full h-7 px-2.5 inline-flex items-center gap-1 transition-colors"
        >
          <Plus className="w-3 h-3" /> Custom
        </button>
      </div>

      {/* Variant rows */}
      {variants.length === 0 ? (
        <div className="mt-4 text-[11px] text-[#A8A29E] italic">
          No options added yet — pick one above.
        </div>
      ) : (
        <div className="mt-4 space-y-1.5">
          <div className="grid grid-cols-[1fr_56px_72px_24px] sm:grid-cols-[1fr_64px_90px_24px] gap-2 fc-label">
            <div>Option</div>
            <div className="text-right">Qty</div>
            <div className="text-right">Rate</div>
            <div />
          </div>
          {variants.map((v) => (
            <div
              key={v.id}
              className="grid grid-cols-[1fr_56px_72px_24px] sm:grid-cols-[1fr_64px_90px_24px] gap-2 items-center"
              data-testid={`${tid}variant-row-${v.id}`}
            >
              <Input
                value={v.label}
                onChange={(e) => updateVariant(v.id, { label: e.target.value })}
                className="fc-input h-8 rounded-md text-xs"
                data-testid={`${tid}variant-label-${v.id}`}
              />
              <Input
                type="number"
                value={v.qty}
                onChange={(e) => updateVariant(v.id, { qty: Number(e.target.value) || 0 })}
                className="fc-input h-8 rounded-md text-xs text-right"
                data-testid={`${tid}variant-qty-${v.id}`}
              />
              <Input
                type="number"
                value={v.rate}
                onChange={(e) => updateVariant(v.id, { rate: Number(e.target.value) || 0 })}
                className="fc-input h-8 rounded-md text-xs text-right"
                data-testid={`${tid}variant-rate-${v.id}`}
              />
              <button
                type="button"
                onClick={() => removeVariant(v.id)}
                data-testid={`${tid}variant-remove-${v.id}`}
                className="w-6 h-6 grid place-items-center text-[#A8A29E] hover:text-[#DC2626] rounded transition-colors"
                aria-label="Remove variant"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <div className="text-[11px] text-[#78716C] pt-1">
            Unit: {unit} · Scope multiplier still applies to the combined total.
          </div>
        </div>
      )}
    </div>
  );
}
